import {
  API_HTTP_METHOD_VALUES,
  type ApiDeliveryEndpointConfig,
} from '@steerfi/shared/types/jsonb/integration-profile.types';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormControl, FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { focusAfterRemoval } from '@/hooks/focus-after-removal';
import { KvEditor } from './kv-editor';

interface EndpointsEditorProps {
  value: Record<string, ApiDeliveryEndpointConfig>;
  onChange: (v: Record<string, ApiDeliveryEndpointConfig>) => void;
  /** True while a name is blank, two endpoints share a name, or a nested header is blank or duplicated. */
  onDuplicateChange?: (hasDuplicate: boolean) => void;
  disabled?: boolean;
}

interface EndpointRow {
  id: string;
  name: string;
  config: ApiDeliveryEndpointConfig;
  /** False until the name is edited or blurred, so a new row does not open with an error. */
  touched: boolean;
}

const rowsFromValue = (value: Record<string, ApiDeliveryEndpointConfig>): EndpointRow[] =>
  Object.entries(value).map(([name, config]) => ({
    id: crypto.randomUUID(),
    name,
    config,
    touched: true,
  }));

const recordFromRows = (
  rows: readonly EndpointRow[]
): Record<string, ApiDeliveryEndpointConfig> | null => {
  const seen = new Set<string>();
  const next: Record<string, ApiDeliveryEndpointConfig> = {};
  for (const row of rows) {
    if (!row.name.trim()) return null;
    if (seen.has(row.name)) return null;
    seen.add(row.name);
    next[row.name] = row.config;
  }
  return next;
};

const duplicateRowIds = (rows: readonly EndpointRow[]): Set<string> => {
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(row.name, (counts.get(row.name) ?? 0) + 1);
  return new Set(
    rows.filter((row) => row.name && (counts.get(row.name) ?? 0) > 1).map((row) => row.id)
  );
};

export const EndpointsEditor: React.FC<EndpointsEditorProps> = ({
  value,
  onChange,
  onDuplicateChange,
  disabled,
}) => {
  const isDisabled = disabled ?? false;
  const [rows, setRows] = useState(() => rowsFromValue(value));
  const [headerDuplicates, setHeaderDuplicates] = useState<Record<string, boolean>>({});
  const lastEmitted = useRef(JSON.stringify(value));
  const onChangeRef = useRef(onChange);
  const onDuplicateChangeRef = useRef(onDuplicateChange);
  const rootRef = useRef<HTMLDivElement>(null);
  onChangeRef.current = onChange;
  onDuplicateChangeRef.current = onDuplicateChange;

  const duplicates = duplicateRowIds(rows);
  const namesDuplicate = duplicates.size > 0;
  const headersDuplicate = Object.values(headerDuplicates).some(Boolean);
  const hasBlankName = rows.some((row) => row.name.trim() === '');
  const hasDuplicate = namesDuplicate || headersDuplicate || hasBlankName;

  useEffect(() => {
    const incoming = JSON.stringify(value);
    if (incoming === lastEmitted.current) return;
    lastEmitted.current = incoming;
    setRows(rowsFromValue(value));
    // New row ids would orphan the previous map and leave Save disabled.
    setHeaderDuplicates({});
  }, [value]);

  useEffect(() => {
    onDuplicateChangeRef.current?.(hasDuplicate);
    if (namesDuplicate || hasBlankName) return;

    const record = recordFromRows(rows);
    if (!record) return;
    const serialized = JSON.stringify(record);
    if (serialized === lastEmitted.current) return;
    lastEmitted.current = serialized;
    onChangeRef.current(record);
  }, [rows, hasDuplicate, namesDuplicate, hasBlankName]);

  useEffect(() => {
    return () => onDuplicateChangeRef.current?.(false);
  }, []);

  const updateRow = (id: string, patch: Partial<ApiDeliveryEndpointConfig>) => {
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, config: { ...row.config, ...patch } } : row))
    );
  };

  const deleteField = (id: string, field: keyof ApiDeliveryEndpointConfig) => {
    setRows((current) =>
      current.map((row) => {
        if (row.id !== id) return row;
        const config = { ...row.config };
        delete config[field];
        return { ...row, config };
      })
    );
  };

  const removeRow = (id: string) => {
    const ids = rows.map((row) => row.id);
    focusAfterRemoval(rootRef.current, ids, ids.indexOf(id), () => {
      setHeaderDuplicates((current) => {
        if (!(id in current)) return current;
        const next = { ...current };
        delete next[id];
        return next;
      });
      setRows((current) => current.filter((row) => row.id !== id));
    });
  };

  return (
    <div ref={rootRef} className="space-y-2">
      <Label className="text-xs text-muted-foreground">Endpoints</Label>
      {rows.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No endpoints yet.</p>
      )}
      <div className="space-y-2">
        {rows.map((row, index) => {
          const blank = row.name.trim() === '';
          const required = blank && row.touched;
          const duplicate = duplicates.has(row.id);
          const errorId = `${row.id}-error`;
          const endpointName = blank ? String(index + 1) : row.name;
          const forEndpoint = <span className="sr-only"> for endpoint {endpointName}</span>;
          const nameInputId = `endpoint-${row.id}-name`;
          return (
            <div key={row.id} className="rounded-lg border p-3 space-y-2">
              <div className="space-y-1">
                <Label htmlFor={nameInputId} className="text-[11px] text-muted-foreground">
                  Name
                  <span className="text-destructive" aria-hidden="true">
                    *
                  </span>
                  {forEndpoint}
                </Label>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1 space-y-1">
                    <Input
                      id={nameInputId}
                      className="h-7 text-xs font-mono"
                      disabled={isDisabled}
                      placeholder="operationName"
                      value={row.name}
                      onChange={(e) =>
                        setRows((current) =>
                          current.map((item) =>
                            item.id === row.id
                              ? { ...item, name: e.target.value, touched: true }
                              : item
                          )
                        )
                      }
                      onBlur={() => {
                        if (row.touched) return;
                        setRows((current) =>
                          current.map((item) =>
                            item.id === row.id ? { ...item, touched: true } : item
                          )
                        );
                      }}
                      aria-invalid={required || duplicate || undefined}
                      aria-required={true}
                      aria-describedby={required || duplicate ? errorId : undefined}
                    />
                    <p
                      id={errorId}
                      data-error-slot=""
                      className="min-h-[1.5em] text-[11px] text-destructive"
                    >
                      {required ? 'Name is required' : duplicate ? 'Duplicate name' : ''}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                    disabled={isDisabled}
                    aria-label={`Remove endpoint ${endpointName}`}
                    data-remove-row={row.id}
                    onClick={() => removeRow(row.id)}
                  >
                    <IconTrash size={13} aria-hidden />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <FormField
                  label={<>Path{forEndpoint}</>}
                  required
                  labelClassName="text-[11px] text-muted-foreground"
                  className="gap-1"
                >
                  <FormControl>
                    <Input
                      className="h-7 text-xs font-mono"
                      disabled={isDisabled}
                      placeholder="/v1/operation"
                      value={row.config.path}
                      onChange={(e) => updateRow(row.id, { path: e.target.value })}
                    />
                  </FormControl>
                </FormField>
                <FormField
                  label={<>Method{forEndpoint}</>}
                  labelClassName="text-[11px] text-muted-foreground"
                  className="gap-1"
                >
                  <Select
                    disabled={isDisabled}
                    value={row.config.method ?? ''}
                    onValueChange={(v) => {
                      if (v)
                        updateRow(row.id, {
                          method: v as (typeof API_HTTP_METHOD_VALUES)[number],
                        });
                      else deleteField(row.id, 'method');
                    }}
                  >
                    <FormControl>
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder="Inherit" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {API_HTTP_METHOD_VALUES.map((m) => (
                        <SelectItem key={m} value={m} className="text-xs">
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <FormField
                  label={<>Timeout (ms){forEndpoint}</>}
                  labelClassName="text-[11px] text-muted-foreground"
                  className="gap-1"
                >
                  <FormControl>
                    <Input
                      type="number"
                      className="h-7 text-xs"
                      disabled={isDisabled}
                      placeholder="Inherit"
                      value={row.config.timeoutMs ?? ''}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        if (e.target.value && !isNaN(n)) updateRow(row.id, { timeoutMs: n });
                        else if (!e.target.value) deleteField(row.id, 'timeoutMs');
                      }}
                    />
                  </FormControl>
                </FormField>
              </div>
              <KvEditor
                label="Headers (override)"
                ownerLabel={`endpoint ${endpointName}`}
                disabled={isDisabled}
                value={row.config.headers ?? {}}
                onDuplicateChange={(duplicateHeaders) =>
                  setHeaderDuplicates((current) => {
                    if (Boolean(current[row.id]) === duplicateHeaders) return current;
                    return { ...current, [row.id]: duplicateHeaders };
                  })
                }
                onChange={(headers) => {
                  if (Object.keys(headers).length) updateRow(row.id, { headers });
                  else deleteField(row.id, 'headers');
                }}
              />
            </div>
          );
        })}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-xs gap-1"
        disabled={isDisabled}
        data-add-row=""
        onClick={() =>
          setRows((current) => [
            ...current,
            { id: crypto.randomUUID(), name: '', config: { path: '' }, touched: false },
          ])
        }
      >
        <IconPlus size={12} />
        Add endpoint
      </Button>
    </div>
  );
};
